import { makeStyles } from "tss-react/mui";
import { MiAlert, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
} from "@miplatform/constants";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import * as mdi from "@mdi/js";
import { mdiPencil } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { WorkTemplateBaseList } from "./WorkTemplateBaseList";
import { useEditTimeWindowsModalViewModel } from "./useEditTimeWindowsModalViewModel";

export const EditTimeWindowsModal = ({
  open,
  onClose,
  onSuccess,
  workTemplateBases,
}: EditTimeWindowsModalProps) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const {
    draftWindowsByTemplateId,
    overlappingByTemplateId,
    dirtyTemplateIds,
    hasValidationErrors,
    isSaving,
    handleClose,
    handleAddWindow,
    handleRemoveWindow,
    getWindowFields,
    handleSave,
  } = useEditTimeWindowsModalViewModel({
    open,
    workTemplateBases,
    onClose,
    onSuccess,
  });

  const filterOutWorkTemplateBases = workTemplateBases.filter(
    (x) => !x.isCustom
  );

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={600}
      titleIcon={mdiPencil}
      leftIconColor={theme.palette.primary.main}
      title="Edit Time Windows"
      description="Add, change, or remove time windows"
      loading={isSaving}
      actions={[
        {
          name: "Close",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
          disabled: isSaving,
        },
        {
          name: "Save",
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSave,
          disabled:
            isSaving || dirtyTemplateIds.length === 0 || hasValidationErrors,
        },
      ]}
    >
      <Box className={classes.content}>
        <Box>
          <MiAlert
            isOpen
            hideCloseButton
            variant="warning"
            description="Any changes to time windows will reset all future assignments"
            leftIcon={ICONS_WEB.ALERT_OUTLINE as keyof typeof mdi}
            iconColor={theme.palette.warning.main}
            sx={{
              backgroundColor: theme.palette.warning.light,
              border: `1px solid ${theme.palette.warning.main}`,
              paddingTop: theme.spacing(0),
              paddingBottom: theme.spacing(0),
              width: "100%",
            }}
          />
        </Box>
        <Box className={classes.workTemplateBases}>
          {filterOutWorkTemplateBases.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No work order templates are attached to this calendar set.
            </Typography>
          ) : (
            <WorkTemplateBaseList
              workTemplateBases={filterOutWorkTemplateBases}
              draftWindowsByTemplateId={draftWindowsByTemplateId}
              overlappingByTemplateId={overlappingByTemplateId}
              isSaving={isSaving}
              onAddWindow={handleAddWindow}
              onRemoveWindow={handleRemoveWindow}
              getWindowFields={getWindowFields}
            />
          )}
        </Box>
      </Box>
    </MiModal>
  );
};

interface EditTimeWindowsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  workTemplateBases: ICalendarWorkTemplateBase[];
}

const useStyles = makeStyles()((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    maxHeight: "60vh",
    overflowY: "auto",
    paddingRight: theme.spacing(1),
  },
  workTemplateBases: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
}));
